/**
 * 앱 셸 — 3분할 (DESIGN §8.6.8).
 *
 *   global-nav   검정 44px. 브랜드 + 빌드 정보
 *   좌 레일      정의 · 페이지 · 필터 · 이 화면의 범위
 *   중앙         본문만
 *   우 패널      그 화면에만 있는 맥락 (지도·분석 전용)
 *
 * v0.3 은 상단 2단 내비였다. 화면이 7개로 늘면서 상단이 세 층(44 + 52 + 44)
 * 으로 쌓여 본문 시작 전에 140px 을 쓰고, 탭·정의·필터가 가로로 흩어져
 * "무엇을 보는가"와 "무엇을 걸었는가"가 한눈에 안 들어왔다. 세로는 스크롤로
 * 벌 수 있지만 가로는 못 번다 — 좌 레일은 스크롤 없이 상시 보이는 자리다.
 *
 * 어떤 숫자를 보든 "어느 빌드의, 어떤 고장 정의로 나온 값인가"가 상시
 * 붙어 있어야 한다는 §8.5 재현성 요건은 그대로다 — 빌드는 gnav 에,
 * 고장 정의는 좌 레일 첫 구획에 있다.
 */

import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { BuildControl } from '@/components/BuildControl'
import { Rail } from '@/components/Rail'
import { fetchPlain } from '@/lib/api'
import { SideSlotCtx } from '@/lib/sideSlot'
import type { ManifestData } from '@/lib/types'
import { AgingPage } from '@/pages/AgingPage'
import { DeteriorationPage } from '@/pages/DeteriorationPage'
import { MapPage } from '@/pages/MapPage'
import { OverviewPage } from '@/pages/OverviewPage'
import { PlacesPage } from '@/pages/PlacesPage'
import { QualityPage } from '@/pages/QualityPage'
import { ScorecardPage } from '@/pages/ScorecardPage'
import { ReconcilePage } from '@/pages/ReconcilePage'
import { AnalysisPage } from '@/pages/AnalysisPage'
import { CohortPage } from '@/pages/CohortPage'
import { UsagePage } from '@/pages/UsagePage'
import './App.css'

/**
 * 화면은 **두 대분류 + 근거**로 묶인다 (DESIGN §8.0).
 *
 * v0.3 까지는 "질문의 순서"로 한 줄에 세웠다. 8개까지는 순서만으로 읽혔지만
 * 그 순서가 답하는 질문이 실은 두 종류다 — *제대로 도는가*(고장)와 *실제로
 * 쓰이는가*(사용량). 지표도 분모도 다르다. 한 줄에 섞으면 "가동률 99%" 와
 * "이용률 36%" 가 같은 층위로 읽혀 **멀쩡하다는 말과 쓰인다는 말이 구분되지
 * 않는다.**
 *
 * 대분류 안의 순서는 여전히 질문의 순서다. 근거(데이터 품질)를 대분류 밖에
 * 두는 것은 덜 중요해서가 아니라 **양쪽 숫자를 함께 되짚는 자리**이기 때문이다 —
 * 한쪽에 넣으면 다른 쪽이 근거를 잃는다.
 */
const GROUPS = [
  { key: 'fault', title: '고장 분석', lead: '제대로 도는가' },
  { key: 'usage', title: '사용량 분석', lead: '실제로 쓰이는가' },
  { key: 'base', title: '근거', lead: '' },
] as const

export type GroupKey = (typeof GROUPS)[number]['key']

const PAGES = [
  { key: 'overview', title: '개요', lead: '비교로 읽는 가동률', side: false, group: 'fault' },
  // 'aging' 키는 그대로 둔다 — 주소(#/aging)가 바뀌지 않게. 이름만 「연식 비교」로 바꿨다 (§8.10)
  { key: 'aging', title: '연식 비교', lead: '연식 코호트를 나란히', side: false, group: 'fault' },
  { key: 'deterioration', title: '노후화 분석', lead: '늙어서 나빠지는가, 말할 수 있는가', side: false, group: 'fault' },
  { key: 'analysis', title: '분석·관계', lead: '누가 누구와 닮았나', side: true, group: 'fault' },
  { key: 'scorecard', title: '스코어카드', lead: '동급과 견주면', side: false, group: 'fault' },
  { key: 'cohort', title: '코호트', lead: '고장은 언제 끝나는가', side: false, group: 'fault' },
  { key: 'reconcile', title: '공표통계 대조', lead: '정의를 바꾸면 얼마나', side: false, group: 'fault' },
  { key: 'usage', title: '이용 개요', lead: '얼마나 쓰이는가', side: false, group: 'usage' },
  { key: 'places', title: '설치 장소', lead: '어디에 놓였고 어떻게 쓰이나', side: false, group: 'usage' },
  { key: 'map', title: '지도', lead: '전국 어디에서', side: true, group: 'usage' },
  { key: 'quality', title: '데이터 품질', lead: '이 숫자를 믿을 근거', side: false, group: 'base' },
] as const

type PageKey = (typeof PAGES)[number]['key']

function pageFromHash(): PageKey {
  const raw = window.location.hash.replace(/^#\/?/, '')
  return PAGES.some((p) => p.key === raw) ? (raw as PageKey) : 'overview'
}

export default function App() {
  // 페이지는 해시에 둔다 — 필터는 쿼리스트링을 쓰므로 서로 밟지 않는다 (§8.7)
  const [page, setPage] = useState<PageKey>(() =>
    typeof window === 'undefined' ? 'overview' : pageFromHash(),
  )

  useEffect(() => {
    const onHash = () => setPage(pageFromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  // 우 패널 슬롯. ref 콜백이 DOM 노드를 상태로 올려야 자식이 portal 할 수 있다
  const [sideEl, setSideEl] = useState<HTMLElement | null>(null)
  const hasSide = PAGES.find((p) => p.key === page)?.side ?? false

  const { data: manifest } = useQuery({
    queryKey: ['meta', 'manifest'],
    queryFn: ({ signal }) => fetchPlain<ManifestData>('/meta/manifest', signal),
    staleTime: 60 * 1000,
    retry: 1,
  })

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        본문으로 건너뛰기
      </a>

      <header className="gnav">
        <div className="gnav__brand">
          <span className="gnav__mark" aria-hidden="true">
            ⌁
          </span>
          전기차 충전기 상태 분석
        </div>
        <p className="gnav__tag">관제 도구가 아닙니다 — 정확성 &gt; 실시간성</p>
        <div className="gnav__meta">
          {manifest ? (
            <>
              <span className="mono" title={`생성 ${manifest.generated_at}`}>
                {manifest.data_version}
              </span>
              <span className="gnav__dot" aria-hidden="true">
                ·
              </span>
              <span className="num">
                모집단 {manifest.population_n.toLocaleString('ko-KR')}기
              </span>
              <span className="gnav__dot" aria-hidden="true">
                ·
              </span>
            </>
          ) : (
            <span>빌드 정보 없음</span>
          )}
          {/* 버전만 있고 나이가 없어서 "수집이 끊겼다"로 읽혔다 (§7.2.1) */}
          <BuildControl />
        </div>
      </header>

      <div className={`app__body${hasSide ? ' app__body--side' : ''}`}>
        <Rail pages={PAGES} groups={GROUPS} page={page} />

        <main id="main" className="app__main">
          {/* 활성 탭이 곧 현재 페이지 표시다 — 제목을 눈에 두 번 쓰지 않는다 */}
          <h1 className="sr-only">{PAGES.find((p) => p.key === page)?.title}</h1>
          <SideSlotCtx.Provider value={sideEl}>
            {page === 'overview' && <OverviewPage />}
            {page === 'usage' && <UsagePage />}
            {page === 'aging' && <AgingPage />}
            {page === 'deterioration' && <DeteriorationPage />}
            {page === 'places' && <PlacesPage />}
            {page === 'map' && <MapPage />}
            {page === 'quality' && <QualityPage />}
            {page === 'reconcile' && <ReconcilePage />}
            {page === 'analysis' && <AnalysisPage />}
            {page === 'cohort' && <CohortPage />}
            {page === 'scorecard' && <ScorecardPage />}
          </SideSlotCtx.Provider>
        </main>

        {/* 우 패널 — 지도·분석만. 페이지가 <SidePanel> 로 내용을 넣는다 */}
        {hasSide && <aside className="app__side" ref={setSideEl} aria-label="화면 맥락" />}
      </div>

      <footer className="foot">
        <p className="foot__source">
          <strong>출처: 한국환경공단</strong> — 공공누리 제1유형. 기상 자료: 기상청 ASOS 일자료.
        </p>
        <p className="foot__note">
          이 대시보드는 공개 API 관측에 기반한 연구 도구입니다. 관측되지 않은 시간은 0 이 아니라
          결측으로 표시하며, 커버리지 없는 가동률은 표시하지 않습니다.
        </p>
      </footer>
    </div>
  )
}
